//? Note: jurisdiction specific component

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { mapStateToProps, mapDispatchToProps } from '../Store';
import {
    ContentBox,
    ContentBoxHead,
    ContentBoxBody,
    HideFromView,
    getCMSObject,
    formatDate,
    IfLoggedIn,
    IfLoggedOut,
    ErrorMessage,
    CMSModal,
} from 'sg-ui-components';
import './dashboard.scss';

import { Container, Button } from 'react-bootstrap';
import { HashLink } from 'react-router-hash-link';
import promotionConfig from '../promotionConfig';
import Menu from './Menu';
import ClickOutsideComponentEvent from '../utils/ClickOutsideComponentEvent';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleInfo, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { faCircleUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faCircleInfo, faCircleXmark, faCircleUser);
/**********************************************************************
 * Component:  PointsDashboard
 * Purpose:    Dashboard component that shows site wide.
 *             If the user is not loggedin - simply displays a login/register button
 *             If user is logged in - displays points information and a sub-menu of
 *             pages user can now navigate too.
 *
 * Props:       user -  user data store
 *              config - config data store
 *              actions - store actions (apis)
 *
 * APIs used:   userActions.getDashboard() - get Dashboard info
 *
 *  Notes:
 */
const PointsDashboard = ({ user, config, actions, cmsSourceFirebase, notification }) => {
    const [accountNavDisplayed, setAccountNavDisplayed] = useState(false);
    const [aboutToExpire, setAboutToExpire] = useState(false);
    const [modalState, setModalState] = useState({
        show: false,
        contentPathForHeader: '',
        contentPathForBody: '',
    });
    const notificationsPresent = useMemo(() => notification?.data?.length > 0, [notification]);

    // set size of Dashboard based on how many elements are in it.
    let dashboardColSize = 'col-lg-10 col-xl-8 mx-auto';
    if (promotionConfig?.dashboardComponents?.length === 1) {
        dashboardColSize = 'col-md-6 col-lg-5 col-xl-4 mx-auto';
    }

    const showModal = (headerPath, bodyPath) => {
        setModalState({
            show: true,
            contentPathForHeader: headerPath,
            contentPathForBody: bodyPath,
        });
    };

    const hideModal = () => {
        setModalState((prevState) => ({
            ...prevState,
            show: false,
        }));
    };

    //***********************************************************************************
    // Get the dashboard
    const getDashboard = async () => {
        await actions.userActions.getDashboard();
    }; // end getDashboard

    //***********************************************************************************
    // Only call getDashbord for logged in players
    useEffect(() => {
        if (user?.loggedIn) {
            getDashboard();
        }

        return () => {
            setAccountNavDisplayed(false);
            setAboutToExpire(false);
        };
    }, []); // end useEffect

    //***********************************************************************************
    // OnClick handler to show/hide the dashboard navigation
    const showAccountNav = async () => {
        const toggleNav = !accountNavDisplayed;
        setAccountNavDisplayed(toggleNav);
    };

    //***********************************************************************************
    // OnMouseLeave handler to hide the dashboard navigation
    const hideAccountNav = async () => {
        setAccountNavDisplayed(false);
    };

    const InfoButton = ({ onClick, label }) => (
        <Button variant='link' onClick={onClick} label={`${label} Info`} aria-label={`${label} Info`} className='cms-modal-button'>
            <FontAwesomeIcon icon='fa-solid fa-circle-info' />
        </Button>
    );

    //***********************************************************************************
    // Draw Points Widget - navigates to MyActivity when clicked
    const DrawPoints = () => {
        return (
            <div className='points-dashboard-item'>
                <div className={`d-flex flex-row justify-content-center`}>
                    <label htmlFor='drawPoints'>Draw Points</label>
                    <InfoButton
                        onClick={() => showModal('data.pointsDashboard.drawPoints.contentHeaderText', 'data.pointsDashboard.drawPoints.contentHTML')}
                        label='Draw Points'
                    />
                </div>
                <Link className='btn inset' id='drawPoints' to='/my-activity#points-history'>
                    {user.drawPoints}
                </Link>
            </div>
        );
    };

    //***********************************************************************************
    // Prize Points Widget - navigates to MyActivity when clicked
    const PrizePoints = () => {
        return (
            <div className='points-dashboard-item'>
                <div className={`d-flex flex-row justify-content-center`}>
                    <label htmlFor='PrizePoints'>Prize Points</label>
                    <InfoButton
                        onClick={() => showModal('data.pointsDashboard.prizePoints.contentHeaderText', 'data.pointsDashboard.prizePoints.contentHTML')}
                        label='Prize Points'
                    />
                </div>
                <Link className='btn theme-btn inset' id='PrizePoints' to='/my-activity#points-history'>
                    {user.prizePoints}
                </Link>
            </div>
        );
    };

    //***********************************************************************************
    // Expiring (prize) Points Widget - navigates to feedback form when clicked
    const ExpiringPoints = () => {
        // Check if the points are expiring soon.
        const today = new Date();
        if (parseInt(user.pointsExpiringDate)) {
            const expiringDates = new Date(user.pointsExpiringDate * 1000);
            let daysLeft = Math.abs(expiringDates - today);
            daysLeft = Math.floor(daysLeft / (1000 * 60 * 60 * 24));

            if (daysLeft < config.config.days_until_points_expire) {
                setAboutToExpire(true);
            }
        }

        return (
            <div className={`points-dashboard-item mt-3 mt-md-0 ${aboutToExpire ? 'expiring' : ''}`}>
                <div className={`d-flex flex-row justify-content-center`}>
                    <label htmlFor={`ExpiringPoints ${aboutToExpire ? 'expiring' : ''}`}>Expiring Prize Points</label>
                    <InfoButton
                        onClick={() =>
                            showModal('data.pointsDashboard.expiringPrizePoints.contentHeaderText', 'data.pointsDashboard.expiringPrizePoints.contentHTML')
                        }
                        label='Expiring Prize Points'
                    />
                </div>
                <HashLink className={`btn theme-btn inset ${aboutToExpire ? 'expiring' : ''}`} id='ExpiringPoints' to='/feedback#rules'>
                    {user.pfpExpiring}
                </HashLink>
                <small className={`theme-text-muted ${aboutToExpire ? 'expiring' : ''}`}>
                    Expires: {user.pointsExpiringDate ? formatDate?.dateTimeWithMeridiem(user.pointsExpiringDate) : ''}
                </small>
            </div>
        );
    };

    //***********************************************************************************
    // Login Button Widget - redirects to login page
    const LoginButton = () => {
        return (
            <div className='col-6 text-center'>
                <Link className='logged-out-btn btn theme-btn theme-primary' id='Login' to='/login'>
                    Login
                </Link>
            </div>
        );
    };

    //***********************************************************************************
    // Register Button Widget - redirects to registration page
    const RegisterButton = () => {
        return (
            <div className='col-6 text-center'>
                <Link type='button' className='logged-out-btn btn theme-btn theme-secondary' id='Register' to='/register'>
                    Sign Up
                </Link>
            </div>
        );
    };

    //***********************************************************************************
    // My Account Navigation widget -  provides user a list of pages they can go when
    // logged in.   Shows/Hides on click.
    const MyAccount = () => {
        const accountMenuRef = useRef(null);
        const dashboardMenu = getCMSObject('dashboardMenu', 'webMenu');

        return (
            <div ref={accountMenuRef} className='points-dashboard-item my-account position-relative'>
                <label className='d-block'>My Account</label>
                <span className='position-relative d-inline-block'>
                    <FontAwesomeIcon icon='fa-solid fa-circle-user' className='my-user-icon' onClick={showAccountNav} alt='account icon' />
                    {notificationsPresent && <span className='red-dot' />}
                </span>
                <Menu
                    menu_json={dashboardMenu}
                    className={`nav flex-column account-nav ${accountNavDisplayed ? 'd-block' : 'd-none'} `}
                    notificationsPresent={notificationsPresent}
                />
                <ClickOutsideComponentEvent ref={accountMenuRef} callback={hideAccountNav} />
            </div>
        );
    };

    return (
        <HideFromView paths={['/login', '/register', '/forgot-password']}>
            <Container>
                <div className='points-dashboard mt-3'>
                    <IfLoggedIn user={user}>
                        <div className={dashboardColSize}>
                            <ContentBox variant='logged-in-dashboard'>
                                <ContentBoxHead>
                                    <span className='welcome-text'>Welcome</span>
                                    <span className='user-name'> {(user?.player?.firstname ?? user?.player?.first_name) || 'Player'}!</span>
                                </ContentBoxHead>
                                <ContentBoxBody>
                                    {user?.dashboardErrors ? (
                                        <ErrorMessage code={user?.dashboardErrors} collection='data.messages.errorMessages.jsonBlock' />
                                    ) : null}
                                    <div className='row justify-content-between'>
                                        {promotionConfig?.dashboardComponents?.includes('pfd') ? <DrawPoints /> : null}
                                        {promotionConfig?.dashboardComponents?.includes('pfp') ? <PrizePoints /> : null}
                                        {promotionConfig?.dashboardComponents?.includes('expiring') ? <ExpiringPoints /> : null}
                                        <MyAccount />
                                    </div>
                                </ContentBoxBody>
                            </ContentBox>
                        </div>
                    </IfLoggedIn>

                    <IfLoggedOut user={user}>
                        <div className='col-md-8 col-lg-6 col-xl-5 mx-auto'>
                            <ContentBox>
                                <ContentBoxHead>
                                    <span className='welcome-text'>Welcome!</span>
                                </ContentBoxHead>
                                <ContentBoxBody>
                                    <div className='row justify-content-center p-md-3'>
                                        <RegisterButton />
                                        <LoginButton />
                                    </div>
                                </ContentBoxBody>
                            </ContentBox>
                        </div>
                    </IfLoggedOut>
                </div>
                <CMSModal
                    show={modalState.show}
                    onHide={hideModal}
                    contentPathForHeader={modalState.contentPathForHeader}
                    contentPathForBody={modalState.contentPathForBody}
                    cmsSourceFirebase={cmsSourceFirebase}
                />
            </Container>
        </HideFromView>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PointsDashboard);
